export enum PermissionType {
  COMPANY = 'companyPermissions',
  LOCATION = 'locationPermissions',
  ZONE = 'zonePermissions',
  RESOURCE = 'resourcePermissions',
}

export type Permissions = {
  [PermissionType.COMPANY]: PermissionSets;
  [PermissionType.LOCATION]: PermissionSets;
  [PermissionType.ZONE]: PermissionSets;
  [PermissionType.RESOURCE]: ResourcePermissionSets;
  reportIds?: string[];
};

export type ResourcePermissionSets = {
  desks: {
    neighborhood: {
      [id: string]: {
        _p: Array<Permission>;
      };
    };
  };
  maps: {
    draft: {
      [id: string]: {
        _p: Array<Permission>;
      };
    };
  };
};

export type PermissionSets = {
  [id: string]: Array<Permission>;
};

export enum Permission {
  ACCOUNT_MANAGEMENT_BILLING_SETTINGS_MANAGE = 'account_management.billing_settings.manage',
  ACCOUNT_MANAGEMENT_LOCATION_CREATE = 'account_management.location.create',
  ACCOUNT_MANAGEMENT_LOCATION_READ = 'account_management.location.read',
  ACCOUNT_MANAGEMENT_LOCATION_UPDATE = 'account_management.location.update',
  ACCOUNT_MANAGEMENT_DISABLED_LOCATION_READ = 'account_management.disabled_location.read',

  ACCOUNT_MANAGEMENT_LOCATION_GROUP_CREATE = 'account_management.location_group.create',
  ACCOUNT_MANAGEMENT_LOCATION_GROUP_READ = 'account_management.location_group.read',
  ACCOUNT_MANAGEMENT_LOCATION_GROUP_UPDATE = 'account_management.location_group.update',
  ACCOUNT_MANAGEMENT_LOCATION_GROUP_DELETE = 'account_management.location_group.delete',

  ACCOUNT_MANAGEMENT_BILLING_WORKPLACE_TRIAL_CREATE = 'account_management.billing_workplace_trial.create',

  ACCOUNT_MANAGEMENT_COMPANY_CONFIG_READ = 'account_management.company_configuration.read',
  ACCOUNT_MANAGEMENT_COMPANY_CONFIG_UPDATE = 'account_management.company_configuration.update',

  ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_READ = 'account_management.product_settings.read',
  ACCOUNT_MANAGEMENT_PRODUCT_SETTINGS_UPDATE = 'account_management.product_settings.update',

  ANALYTICS_LOCAL_OCCUPANCY_DASH_READ = 'analytics.local_occupancy_dash.read',
  ANALYTICS_LOCAL_OCCUPANCY_DASH_DETAIL_READ = 'analytics.local_occupancy_dash.detail_read',
  ANALYTICS_LOCAL_OCCUPANCY_DASH_EXPORT = 'analytics.local_occupancy_dash.export',
  ANALYTICS_GLOBAL_OCCUPANCY_DASH_READ = 'analytics.global_occupancy_dash.read',
  ANALYTICS_GLOBAL_OCCUPANCY_DASH_DETAIL_READ = 'analytics.global_occupancy_dash.detail_read',
  ANALYTICS_GLOBAL_OCCUPANCY_DASH_EXPORT = 'analytics.global_occupancy_dash.export',
  ANALYTICS_GLOBAL_OCCUPANCY_SETTINGS_READ = 'analytics.global_occupancy_settings.read',
  ANALYTICS_GLOBAL_OCCUPANCY_SETTINGS_UPDATE = 'analytics.global_occupancy_settings.update',

  ANALYTICS_LOCAL_DESKS_DASH_READ = 'analytics.local_desks_dash.read',
  ANALYTICS_LOCAL_DESKS_DASH_EXPORT = 'analytics.local_desks_dash.export',
  ANALYTICS_GLOBAL_DESKS_DASH_READ = 'analytics.global_desks_dash.read',
  ANALYTICS_GLOBAL_DESKS_DASH_EXPORT = 'analytics.global_desks_dash.export',

  ANALYTICS_LOCAL_VISITORS_DASH_READ = 'analytics.local_visitors_dash.read',
  ANALYTICS_LOCAL_VISITORS_DASH_EXPORT = 'analytics.local_visitors_dash.export',
  ANALYTICS_GLOBAL_VISITORS_DASH_READ = 'analytics.global_visitors_dash.read',
  ANALYTICS_GLOBAL_VISITORS_DASH_EXPORT = 'analytics.global_visitors_dash.export',

  ANALYTICS_LOCAL_ROOMS_DASH_READ = 'analytics.local_rooms_dash.read',
  ANALYTICS_LOCAL_ROOMS_DASH_EXPORT = 'analytics.local_rooms_dash.export',
  ANALYTICS_GLOBAL_ROOMS_DASH_READ = 'analytics.global_rooms_dash.read',
  ANALYTICS_GLOBAL_ROOMS_DASH_EXPORT = 'analytics.global_rooms_dash.export',

  ANALYTICS_LOCAL_DELIVERIES_DASH_READ = 'analytics.local_deliveries_dash.read',
  ANALYTICS_LOCAL_DELIVERIES_DASH_EXPORT = 'analytics.local_deliveries_dash.export',
  ANALYTICS_GLOBAL_DELIVERIES_DASH_READ = 'analytics.global_deliveries_dash.read',
  ANALYTICS_GLOBAL_DELIVERIES_DASH_EXPORT = 'analytics.global_deliveries_dash.export',

  ANALYTICS_LOCAL_PROTECT_DASH_READ = 'analytics.local_protect_dash.read',
  ANALYTICS_LOCAL_PROTECT_DASH_EXPORT = 'analytics.local_protect_dash.export',
  ANALYTICS_GLOBAL_PROTECT_DASH_READ = 'analytics.global_protect_dash.read',
  ANALYTICS_GLOBAL_PROTECT_DASH_EXPORT = 'analytics.global_protect_dash.export',

  ANALYTICS_OFFICE_ATTENDANCE_READ = 'analytics.office_attendance.read',
  ANALYTICS_OFFICE_ATTENDANCE_EXPORT = 'analytics.office_attendance.export',

  ANALYTICS_MULTITENANCY_DASH_READ = 'analytics.multitenancy_dash.read',
  ANALYTICS_MULTITENANCY_DASH_EXPORT = 'analytics.multitenancy_dash.export',

  ANALYTICS_GLOBAL_CUSTOM_ANALYTICS_DASH_READ = 'analytics.global_custom_analytics_dash.read',
  ANALYTICS_LOCAL_CUSTOM_ANALYTICS_DASH_READ = 'analytics.local_custom_analytics_dash.read',
  ANALYTICS_GLOBAL_CUSTOM_ANALYTICS_DASH_CUSTOMIZE = 'analytics.global_custom_analytics_dash.customize',
  ANALYTICS_LOCAL_CUSTOM_ANALYTICS_DASH_CUSTOMIZE = 'analytics.local_custom_analytics_dash.customize',

  ANALYTICS_LOCAL_STRATEGIC_INSIGHTS_READ = 'analytics.local_strategic_insights.read',

  MAPS_LIVE_READ = 'maps.live_map.read',
  MAPS_LIVE_UPDATE = 'maps.live_map.update',
  MAPS_LIVE_CREATE = 'maps.live_map.create',
  MAPS_LIVE_DELETE = 'maps.live_map.delete',

  MAPS_FLOOR_READ = 'maps.floor.read',
  MAPS_FLOOR_UPDATE = 'maps.floor.update',
  MAPS_FLOOR_CREATE = 'maps.floor.create',
  MAPS_FLOOR_DELETE = 'maps.floor.delete',

  MAPS_AREA_MAP_READ = 'maps.area_map.read',
  MAPS_AREA_MAP_UPDATE = 'maps.area_map.update',
  MAPS_AREA_MAP_CREATE = 'maps.area_map.create',
  MAPS_AREA_MAP_DELETE = 'maps.area_map.delete',

  MAPS_DRAFT_READ = 'maps.draft.read',
  MAPS_DRAFT_UPDATE = 'maps.draft.update',
  MAPS_DRAFT_CREATE = 'maps.draft.create',
  MAPS_DRAFT_DELETE = 'maps.draft.delete',
  MAPS_DRAFT_FLOOR_UPDATE = 'maps.draft_floor.update',
  MAPS_DRAFT_FLOOR_CREATE = 'maps.draft_floor.create',
  MAPS_DRAFT_FLOOR_DELETE = 'maps.draft_floor.delete',
  MAPS_DRAFT_AREA_MAP_UPDATE = 'maps.draft_area_map.update',
  MAPS_DRAFT_AREA_MAP_CREATE = 'maps.draft_area_map.create',
  MAPS_DRAFT_AREA_MAP_DELETE = 'maps.draft_area_map.delete',

  MAPS_ATTACHMENT_READ = 'maps.attachment.read',
  MAPS_ATTACHMENT_UPDATE = 'maps.attachment.update',
  MAPS_ATTACHMENT_CREATE = 'maps.attachment.create',
  MAPS_ATTACHMENT_DELETE = 'maps.attachment.delete',

  MAPS_FEATURE_READ = 'maps.feature.read',
  MAPS_FEATURE_UPDATE = 'maps.feature.update',
  MAPS_FEATURE_CREATE = 'maps.feature.create',
  MAPS_FEATURE_DELETE = 'maps.feature.delete',

  MAPS_DRAFT_FEATURE_UPDATE = 'maps.draft_feature.update',
  MAPS_DRAFT_FEATURE_CREATE = 'maps.draft_feature.create',
  MAPS_DRAFT_FEATURE_DELETE = 'maps.draft_feature.delete',

  DESKS_DESK_READ = 'desks.desk.read',
  DESKS_DESK_UPDATE = 'desks.desk.update',
  DESKS_DESK_CREATE = 'desks.desk.create',
  DESKS_DESK_DELETE = 'desks.desk.delete',
  DESKS_DESK_IMPORT = 'desks.desk.import',
  DESKS_DESK_EXPORT = 'desks.desk.export',

  DESKS_DRAFT_DESK_UPDATE = 'desks.draft_desk.update',
  DESKS_DRAFT_DESK_CREATE = 'desks.draft_desk.create',
  DESKS_DRAFT_DESK_DELETE = 'desks.draft_desk.delete',

  DESKS_NEIGHBORHOOD_READ = 'desks.neighborhood.read',
  DESKS_NEIGHBORHOOD_UPDATE = 'desks.neighborhood.update',
  DESKS_NEIGHBORHOOD_CREATE = 'desks.neighborhood.create',
  DESKS_NEIGHBORHOOD_DELETE = 'desks.neighborhood.delete',

  DESKS_DESK_SETTING_READ = 'desks.desk_setting.read',
  DESKS_DESK_SETTING_UPDATE = 'desks.desk_setting.update',
  DESKS_DESK_SETTING_CREATE = 'desks.desk_setting.create',
  DESKS_DESK_SETTING_DELETE = 'desks.desk_setting.delete',

  DESKS_AMENITY_READ = 'desks.amenity.read',
  DESKS_AMENITY_UPDATE = 'desks.amenity.update',
  DESKS_AMENITY_CREATE = 'desks.amenity.create',
  DESKS_AMENITY_DELETE = 'desks.amenity.delete',

  DESKS_RESERVATION_READ = 'desks.reservation.read',
  DESKS_RESERVATION_CREATE = 'desks.reservation.create',
  DESKS_RESERVATION_UPDATE = 'desks.reservation.update',
  DESKS_RESERVATION_DELETE = 'desks.reservation.delete',

  DESKS_RESERVATION_LOG_READ = 'desks.reservation_log.read',

  DESKS_TRIAL_REQUEST_CREATE = 'desks.trial_request.create',

  VISITORS_ENTRY_READ_MINE = 'visitors.entry.read_mine',
  VISITORS_ENTRY_READ = 'visitors.entry.read',
  VISITORS_ENTRY_CREATE = 'visitors.entry.create',
  VISITORS_ENTRY_UPDATE = 'visitors.entry.update',
  VISITORS_ENTRY_DELETE = 'visitors.entry.delete',
  VISITORS_ENTRY_EXPORT = 'visitors.entry.export',
  VISITORS_ENTRY_EXPORT_ALL = 'visitors.entry.export_all',

  VISITORS_INVITE_READ_MINE = 'visitors.invite.read_mine',
  VISITORS_INVITE_READ = 'visitors.invite.read',
  VISITORS_INVITE_CREATE = 'visitors.invite.create',
  VISITORS_INVITE_UPDATE = 'visitors.invite.update',
  VISITORS_INVITE_DELETE = 'visitors.invite.delete',
  VISITORS_INVITE_EXPORT = 'visitors.invite.export',

  VISITORS_FLOW_INSPECT = 'visitors.flow.inspect',

  VISITORS_FLOW_CREATE = 'visitors.local_flow.create',
  VISITORS_FLOW_UPDATE = 'visitors.local_flow.update',
  VISITORS_FLOW_DELETE = 'visitors.local_flow.delete',

  VISITORS_GLOBAL_FLOW_CREATE = 'visitors.global_flow.create',
  VISITORS_GLOBAL_FLOW_UPDATE = 'visitors.global_flow.update',
  VISITORS_GLOBAL_FLOW_DELETE = 'visitors.global_flow.delete',

  VISITORS_BLOCKLIST_CREATE = 'visitors.blocklist.create',
  VISITORS_BLOCKLIST_READ = 'visitors.blocklist.read',
  VISITORS_BLOCKLIST_UPDATE = 'visitors.blocklist.update',
  VISITORS_BLOCKLIST_DELETE = 'visitors.blocklist.delete',
  VISITORS_BLOCKLIST_UPLOAD = 'visitors.blocklist.upload',

  VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_READ = 'visitors.invite.visual_compliance_approval.read',
  VISITORS_INVITE_VISUAL_COMPLIANCE_APPROVAL_REVIEW = 'visitors.invite.visual_compliance_approval.review',
  VISITORS_INVITE_BLOCKLIST_APPROVAL_REVIEW = 'visitors.invite.blocklist_approval.review',

  VISITORS_INVITE_APPROVAL_REQUIRED_REVIEW = 'visitors.invite.invite_approval.review',

  VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_READ = 'visitors.entry.visual_compliance_approval.read',
  VISITORS_ENTRY_VISUAL_COMPLIANCE_APPROVAL_REVIEW = 'visitors.entry.visual_compliance_approval.review',
  VISITORS_ENTRY_BLOCKLIST_APPROVAL_REVIEW = 'visitors.entry.blocklist_approval.review',

  VISITORS_WATCHLIST_CREATE = 'visitors.watchlist.create',
  VISITORS_WATCHLIST_READ = 'visitors.watchlist.read',
  VISITORS_WATCHLIST_UPDATE = 'visitors.watchlist.update',
  VISITORS_WATCHLIST_DELETE = 'visitors.watchlist.delete',

  VISITORS_FULL_LEGAL_NAME_SETTING_READ = 'visitors.full_legal_name_setting.read',
  VISITORS_FULL_LEGAL_NAME_SETTING_UPDATE = 'visitors.full_legal_name_setting.update',

  VISITORS_DEVICE_CREATE = 'visitors.device.create',
  VISITORS_DEVICE_READ = 'visitors.device.read',
  VISITORS_DEVICE_UPDATE = 'visitors.device.update',
  VISITORS_DEVICE_DELETE = 'visitors.device.delete',

  VISITORS_PRINTER_CREATE = 'visitors.printer.create',
  VISITORS_PRINTER_READ = 'visitors.printer.read',
  VISITORS_PRINTER_UPDATE = 'visitors.printer.update',
  VISITORS_PRINTER_DELETE = 'visitors.printer.delete',
  // Necessary for gating Devices > Printers nav item, because Location Admin
  // based custom roles without View Devices permission set could still view
  // printers when gating by 'visitors.printer.read', since they actually get
  // 'visitors.printer.read' from the entries and invites update permission sets
  VISITORS_PRINTER_NAV = 'visitors.printer.nav',

  VISITORS_PRINTER_CONNECTION_DELETE = 'visitors.printer_connection.delete',
  VISITORS_PRINTER_CONNECTION_READ = 'visitors.printer_connection.read',

  VISITORS_PRINTER_PASSCODE_CREATE = 'visitors.printer_passcode.create',
  VISITORS_GLOBAL_PRINTER_PASSCODE_CREATE = 'visitors.global_printer_passcode.create',

  VISITORS_GLOBAL_PRINTER_CONNECTION_DELETE = 'visitors.global_printer_connection.delete',

  VISITORS_QUESTIONNAIRE_CREATE = 'visitors.questionnaire.create',
  VISITORS_QUESTIONNAIRE_READ = 'visitors.questionnaire.read',
  VISITORS_QUESTIONNAIRE_UPDATE = 'visitors.questionnaire.update',
  VISITORS_QUESTIONNAIRE_DELETE = 'visitors.questionnaire.delete',
  VISITORS_QUESTIONNAIRE_NAV = 'visitors.questionnaire.nav',

  VISITORS_SECURITY_DESK_READ = 'visitors.security_desk.read',

  VISITORS_VFD_CONFIGURATION_READ = 'visitors.vfd_configuration.read',
  VISITORS_VFD_CONFIGURATION_UPDATE = 'visitors.vfd_configuration.update',

  VISITORS_VISITOR_PROFILE_READ = 'visitors.visitor_profile.read',
  VISITORS_VISITOR_PROFILE_READ_VISITS = 'visitors.visitor_profile.read_visits',
  VISITORS_VISITOR_PROFILE_READ_VISITS_GLOBAL = 'visitors.visitor_profile.read_visits_global',

  VISITORS_LOCATION_CONFIG_UPDATE_HOST_NOTIFICATIONS = 'visitors.location_configuration.update_host_notifications',
  VISITORS_LOCATION_CONFIG_UPDATE_INVITES = 'visitors.location_configuration.update_invites',
  VISITORS_LOCATION_CONFIG_UPDATE_SECURITY_DESK_LINK = 'visitors.location_configuration.update_security_desk_link',
  VISITORS_FALLBACK_CONTACT_READ = 'visitors.fallback_contact.read',
  VISITORS_FALLBACK_CONTACT_CREATE = 'visitors.fallback_contact.create',
  VISITORS_FALLBACK_CONTACT_UPDATE = 'visitors.fallback_contact.update',
  VISITORS_FALLBACK_CONTACT_DELETE = 'visitors.fallback_contact.delete',

  // Used to show/hide email/sms message editors. Future epic: Add to `visitors.email_guide.manage_set` in Authz
  VISITORS_LOCATION_CONFIG_UPDATE_VISITOR_NOTIFICATIONS = 'visitors.location_configuration.update_visitor_notifications',

  // Location Settings Page Permissions
  VISITORS_LOCATION_CONFIGURATION_UPDATE_ADDRESS = 'visitors.location_configuration.update_address',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_BRANDING = 'visitors.location_configuration.update_branding',
  VISITORS_LOCATION_CONFIGURATION_GENERATE_KEY = 'visitors.location_configuration.generate_key',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_LANGUAGE = 'visitors.location_configuration.update_language',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_DISABLED = 'visitors.location_configuration.update_disabled',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_CAPACITY = 'visitors.location_configuration.update_capacity',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_SIGN_OUT = 'visitors.location_configuration.update_sign_out',
  VISITORS_LOCATION_CONFIGURATION_UPDATE_WORKPLACE_UTILIZATION = 'visitors.location_configuration.update_workplace_utilization',
  VISITORS_LOCATION_CONFIGURATION_MANAGE_TENANCY = 'visitors.location_configuration.manage_tenancy',
  VISITORS_GDPR_CONFIGURATION_READ = 'visitors.gdpr_configuration.read',
  VISITORS_GDPR_CONFIGURATION_CREATE = 'visitors.gdpr_configuration.create',
  VISITORS_GDPR_CONFIGURATION_UPDATE = 'visitors.gdpr_configuration.update',
  VISITORS_GDPR_CONFIGURATION_DELETE = 'visitors.gdpr_configuration.delete',

  // Visitor Emails and Guides Permissions
  VISITORS_LOCATION_CONFIGURATION_MANAGE_EMAIL = 'visitors.location_configuration.manage_email',
  VISITORS_MAILER_TEMPLATE_MAPPING_CREATE = 'visitors.mailer_template_mapping.create',
  VISITORS_MAILER_TEMPLATE_MAPPING_DELETE = 'visitors.mailer_template_mapping.delete',
  VISITORS_MAILER_TEMPLATE_MAPPING_READ = 'visitors.mailer_template_mapping.read',
  VISITORS_MAILER_TEMPLATE_MAPPING_UPDATE = 'visitors.mailer_template_mapping.update',
  VISITORS_VISITOR_GUIDE_SECTION_READ = 'visitors.visitor_guide_section.read',
  VISITORS_VISITOR_GUIDE_SECTION_UPDATE = 'visitors.visitor_guide_section.update',
  VISITORS_VISITOR_SURVEY_CONFIGURATION_READ = 'visitors.visitor_survey_configuration.read',
  VISITORS_VISITOR_SURVEY_CONFIGURATION_UPDATE = 'visitors.visitor_survey_configuration.update',

  USER_MANAGEMENT_ADMIN_READ = 'user_management.admin.read',
  USER_MANAGEMENT_ADMIN_CREATE = 'user_management.admin.create',
  USER_MANAGEMENT_ADMIN_UPDATE = 'user_management.admin.update',
  USER_MANAGEMENT_ADMIN_DELETE = 'user_management.admin.delete',

  USER_MANAGEMENT_COMPANY_ADMIN_CREATE = 'user_management.company_admin.create',
  USER_MANAGEMENT_COMPANY_ADMIN_UPDATE = 'user_management.company_admin.update',
  USER_MANAGEMENT_COMPANY_ADMIN_DELETE = 'user_management.company_admin.delete',

  USER_MANAGEMENT_LOCATION_ADMIN_CREATE = 'user_management.location_admin.create',
  USER_MANAGEMENT_LOCATION_ADMIN_UPDATE = 'user_management.location_admin.update',
  USER_MANAGEMENT_LOCATION_ADMIN_DELETE = 'user_management.location_admin.delete',

  USER_MANAGEMENT_CONNECT_ADMIN_CREATE = 'user_management.connect_admin.create',
  USER_MANAGEMENT_CONNECT_ADMIN_UPDATE = 'user_management.connect_admin.update',
  USER_MANAGEMENT_CONNECT_ADMIN_DELETE = 'user_management.connect_admin.delete',

  USER_MANAGEMENT_CUSTOM_ROLE_READ = 'user_management.custom_role.read',
  USER_MANAGEMENT_CUSTOM_ROLE_CREATE = 'user_management.custom_role.create',
  USER_MANAGEMENT_CUSTOM_ROLE_UPDATE = 'user_management.custom_role.update',
  USER_MANAGEMENT_CUSTOM_ROLE_DELETE = 'user_management.custom_role.delete',

  USER_MANAGEMENT_EMPLOYEE_READ = 'user_management.employee.read',
  USER_MANAGEMENT_EMPLOYEE_CREATE = 'user_management.employee.create',
  USER_MANAGEMENT_EMPLOYEE_UPDATE = 'user_management.employee.update',
  USER_MANAGEMENT_EMPLOYEE_DELETE = 'user_management.employee.delete',
  USER_MANAGEMENT_EMPLOYEE_IMPORT = 'user_management.employee.import',
  USER_MANAGEMENT_EMPLOYEE_EXPORT = 'user_management.employee.export',

  USER_MANAGEMENT_EMPLOYEE_LOCATION_READ = 'user_management.employee_location.read',
  USER_MANAGEMENT_EMPLOYEE_LOCATION_CREATE = 'user_management.employee_location.create',
  USER_MANAGEMENT_EMPLOYEE_LOCATION_UPDATE = 'user_management.employee_location.update',
  USER_MANAGEMENT_EMPLOYEE_LOCATION_DELETE = 'user_management.employee_location.delete',

  USER_MANAGEMENT_ACTIVITY_LOG_READ = 'user_management.activity_log.read',

  COMMUNICATIONS_ANNOUNCEMENT_CREATE = 'communications.announcement.create',
  COMMUNICATIONS_ANNOUNCEMENT_READ = 'communications.announcement.read',
  COMMUNICATIONS_ANNOUNCEMENT_UPDATE = 'communications.announcement.update',
  COMMUNICATIONS_ANNOUNCEMENT_DELETE = 'communications.announcement.delete',

  COMMUNICATIONS_ANNOUNCEMENT_TEMPLATE_CREATE = 'communications.announcement_template.create',
  COMMUNICATIONS_ANNOUNCEMENT_TEMPLATE_READ = 'communications.announcement_template.read',
  COMMUNICATIONS_ANNOUNCEMENT_TEMPLATE_UPDATE = 'communications.announcement_template.update',
  COMMUNICATIONS_ANNOUNCEMENT_TEMPLATE_DELETE = 'communications.announcement_template.delete',

  COMMUNICATIONS_SETTING_CREATE = 'communications.communication_setting.create',
  COMMUNICATIONS_SETTING_READ = 'communications.communication_setting.read',
  COMMUNICATIONS_SETTING_UPDATE = 'communications.communication_setting.update',
  COMMUNICATIONS_SETTING_DELETE = 'communications.communication_setting.delete',

  SCREENS_CONTENT_CREATE = 'screens.content.create',
  SCREENS_CONTENT_READ = 'screens.content.read',
  SCREENS_CONTENT_UPDATE = 'screens.content.update',
  SCREENS_CONTENT_DELETE = 'screens.content.delete',

  SCREENS_DEVICE_CREATE = 'screens.device.create',
  SCREENS_DEVICE_READ = 'screens.device.read',
  SCREENS_DEVICE_UPDATE = 'screens.device.update',
  SCREENS_DEVICE_DELETE = 'screens.device.delete',

  APPS_LOCATION_APP_CREATE = 'apps.location_app.create',
  APPS_LOCATION_APP_READ = 'apps.location_app.read',
  APPS_LOCATION_APP_UPDATE = 'apps.location_app.update',
  APPS_LOCATION_APP_DELETE = 'apps.location_app.delete',

  APPS_COMPANY_APP_CREATE = 'apps.company_app.create',
  APPS_COMPANY_APP_READ = 'apps.company_app.read',
  APPS_COMPANY_APP_UPDATE = 'apps.company_app.update',
  APPS_COMPANY_APP_DELETE = 'apps.company_app.delete',

  DELIVERIES_DELIVERY_CREATE = 'deliveries.delivery.create',
  DELIVERIES_DELIVERY_READ = 'deliveries.delivery.read',
  DELIVERIES_DELIVERY_READ_MINE = 'deliveries.delivery.read_mine',
  DELIVERIES_DELIVERY_UPDATE = 'deliveries.delivery.update',
  DELIVERIES_DELIVERY_DELETE = 'deliveries.delivery.delete',
  DELIVERIES_DELIVERY_EXPORT = 'deliveries.delivery.export',

  DELIVERIES_DELIVERY_AREA_CREATE = 'deliveries.delivery_area.create',
  DELIVERIES_DELIVERY_AREA_READ = 'deliveries.delivery_area.read',
  DELIVERIES_DELIVERY_AREA_UPDATE = 'deliveries.delivery_area.update',
  DELIVERIES_DELIVERY_AREA_DELETE = 'deliveries.delivery_area.delete',

  DELIVERIES_DELIVERY_SETTINGS_CREATE = 'deliveries.delivery_settings.create',
  DELIVERIES_DELIVERY_SETTINGS_READ = 'deliveries.delivery_settings.read',
  DELIVERIES_DELIVERY_SETTINGS_UPDATE = 'deliveries.delivery_settings.update',
  DELIVERIES_DELIVERY_SETTINGS_DELETE = 'deliveries.delivery_settings.delete',

  DELIVERIES_DELIVERY_DEVICE_CREATE = 'deliveries.delivery_device.create',
  DELIVERIES_DELIVERY_DEVICE_READ = 'deliveries.delivery_device.read',
  DELIVERIES_DELIVERY_DEVICE_UPDATE = 'deliveries.delivery_device.update',
  DELIVERIES_DELIVERY_DEVICE_DELETE = 'deliveries.delivery_device.delete',

  WORKPLACE_EMPLOYEE_INVITE_CREATE = 'workplace.employee_invite.create',
  WORKPLACE_EMPLOYEE_INVITE_READ = 'workplace.employee_invite.read',
  WORKPLACE_EMPLOYEE_INVITE_UPDATE = 'workplace.employee_invite.update',
  WORKPLACE_EMPLOYEE_INVITE_DELETE = 'workplace.employee_invite.delete',

  WORKPLACE_EMPLOYEE_FLOW_READ = 'workplace.employee_flow.read',
  WORKPLACE_EMPLOYEE_FLOW_INSPECT = 'workplace.employee_flow.inspect',
  WORKPLACE_EMPLOYEE_FLOW_CREATE = 'workplace.employee_flow.create',
  WORKPLACE_EMPLOYEE_FLOW_UPDATE = 'workplace.employee_flow.update',
  WORKPLACE_EMPLOYEE_FLOW_DELETE = 'workplace.employee_flow.delete',
  WORKPLACE_EMPLOYEE_INVITE_EXPORT = 'workplace.employee_invite.export_all',

  WORKPLACE_EMPLOYEE_REGISTRATION_CONFIGURATION_UPDATE = 'workplace.employee_registration_configuration.update',
  WORKPLACE_USER_DOCUMENT_TEMPLATE_CONFIGURATION_UPDATE = 'workplace.user_document_template_configuration.update',
}

export const MANAGE_LOCATION_ADMIN_PERMISSIONS = [
  Permission.USER_MANAGEMENT_LOCATION_ADMIN_CREATE,
  Permission.USER_MANAGEMENT_LOCATION_ADMIN_UPDATE,
  Permission.USER_MANAGEMENT_LOCATION_ADMIN_DELETE,
];

export const MANAGE_COMPANY_ADMIN_PERMISSIONS = [
  Permission.USER_MANAGEMENT_COMPANY_ADMIN_CREATE,
  Permission.USER_MANAGEMENT_COMPANY_ADMIN_UPDATE,
  Permission.USER_MANAGEMENT_COMPANY_ADMIN_DELETE,
];

export const MANAGE_CONNECT_ADMIN_PERMISSIONS = [
  Permission.USER_MANAGEMENT_CONNECT_ADMIN_CREATE,
  Permission.USER_MANAGEMENT_CONNECT_ADMIN_UPDATE,
  Permission.USER_MANAGEMENT_CONNECT_ADMIN_DELETE,
];
